import React, {useEffect, useState} from 'react';

import 'moment-timezone';
import {getBooleanParamOrDefault, getStringParamOrDefault, updateQueryStringForState} from '../../utils/urlBuilder';
import IngestionLogsContent from "./IngestionLogsContent";
import Page from "../Components/Page";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import Checkbox from "../Components/Checkbox";
import PropTypes from "prop-types";
import IngestionLogsPageKey from "./IngestionLogsPageKey";
import {getUserTypeFromLocalToken, userType} from "../../auth/accessTokenValidator";
import AirflowMetrics from "./AirflowMetrics";
import moment from "moment/moment";
import {formatDate, todayChicago} from "../Alerts/MissingData/filters";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import {makeApiCall} from "../../Api/makeApiCall";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import DateRangeInput from "../Components/DateRangePicker";
import SweeperDagStatus from "./SweeperDagStatus";

export const IngestionLogsPage = ({retailerId = undefined, displayHelmetHeader = true, showAirflowMetrics = true}) => {
    const userTypeFromToken = getUserTypeFromLocalToken()
    const getDateForDaysAgo = (fromDate, numberOfDaysAgo) => moment(fromDate).subtract(numberOfDaysAgo, 'days').format('YYYY-MM-DD');

    const todayDate = formatDate(todayChicago());
    const yesterdayDate = getDateForDaysAgo(todayDate, 1);
    const thirtyDaysAgoDate = getDateForDaysAgo(todayDate, 30);

    const defaultState = {
        includeSuccess: getBooleanParamOrDefault('includeSuccess', false),
        includeShortCircuit: getBooleanParamOrDefault('includeShortCircuit', false),
        includeBulkOnly: getBooleanParamOrDefault('includeBulkOnly', false),
        includeCurrentlyRunning: getBooleanParamOrDefault('includeCurrentlyRunning', false),
        includeFailures: getBooleanParamOrDefault('includeFailures', true),
        hideOnboarding: getBooleanParamOrDefault('hideOnboarding', userTypeFromToken !== userType.locatorsUser),
        endDate: getStringParamOrDefault('endDate', todayDate),
        startDate: getStringParamOrDefault('startDate', yesterdayDate),
        retailerIds: retailerId,
        submitCount: 0
    }
    const [includeSuccess, updateIncludeSuccess] = useState(defaultState['includeSuccess']);
    const [includeShortCircuit, updateIncludeShortCircuit] = useState(defaultState['includeShortCircuit']);
    const [includeBulkOnly, updateIncludeBulkOnly] = useState(defaultState['includeBulkOnly']);
    const [includeCurrentlyRunning, updateIncludeCurrentlyRunning] = useState(defaultState['includeCurrentlyRunning']);
    const [includeFailures, updateIncludeFailures] = useState(defaultState['includeFailures']);
    const [hideOnboarding, updateHideOnboarding] = useState(defaultState['hideOnboarding']);
    const [startDate, updateStartDate] = useState(defaultState['startDate']);
    const [endDate, updateEndDate] = useState(defaultState['endDate']);
    const [ingestionLogsParameters, updateIngestionLogsParameters] = useState(defaultState);
    const [ingestionLogsData, setIngestionLogsData] = useState({metadata:[]})
    const [loading, setLoading] = useState(true);
    const [salesForceLoading, setSalesForceLoading] = useState(true);
    const [salesforceRetailers, setSalesforceRetailers] = useState([]);

    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/ingestionLogs/`;

    const urlParameters = {
        includeSuccess,
        includeShortCircuit,
        includeBulkOnly,
        includeCurrentlyRunning,
        includeFailures,
        hideOnboarding,
        startDate: startDate || yesterdayDate,
        endDate: endDate || todayDate,
    };

    updateQueryStringForState(urlParameters)

    function getStartDateForApi(ingestionLogsParameters) {
        const startDateMoment = moment(ingestionLogsParameters.startDate);
        const endDateMoment = moment(ingestionLogsParameters.endDate);

        const days = endDateMoment.diff(startDateMoment, 'days');
        const now = moment.utc();
        const formatedStartDate = moment(ingestionLogsParameters.endDate)
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second())
            .millisecond(now.millisecond())
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')

        ingestionLogsParameters['startDate'] = `"${formatedStartDate}"`;

        ingestionLogsParameters['days'] = days;
        delete ingestionLogsParameters.endDate
        return ingestionLogsParameters;
    }

    function shouldDisableButton() {
        return !(includeFailures || includeSuccess || includeCurrentlyRunning) ||
            (startDate >= endDate) ||
            (startDate < thirtyDaysAgoDate) ||
            (endDate < thirtyDaysAgoDate) ||
            (endDate > todayDate);
    }

    useEffect(() => {
        setLoading(true)
        makeApiCall(endpointUrl, getStartDateForApi(ingestionLogsParameters))
            .then(results => setIngestionLogsData(results))
            .finally(() => setLoading(false));
    }, [ingestionLogsParameters])

    useEffect(() => {
        setSalesForceLoading(true)
        const salesforceRetailersQuery = `query { salesforceRetailers { retailerId onboardingStatus } }`
        makeApolloClientCall(salesforceRetailersQuery)
            .then(results => setSalesforceRetailers(results))
            .finally(() => setSalesForceLoading(false));
    }, []);

    return (
        <Page name={'File Logs'} displayHelmetHeader={displayHelmetHeader}
              breadCrumbs={<Breadcrumbs items={[{label: 'Logs'}, {label: 'File Logs'}]}/>}>
            <SweeperDagStatus />
            {showAirflowMetrics && <AirflowMetrics />}
            <div id={'check-boxes'}>
                <div className={'checkbox-group'}>
                    <h6 className={'checkbox-header'}>Duration Range</h6>
                    <div className={'date-range-group'}>
                        <DateRangeInput
                            data-testid="ingestion-logs-date-range"
                            startDate={startDate}
                            endDate={endDate}
                            onChange={({fromFormatted,toFormatted})=> {
                                updateStartDate(fromFormatted);
                                updateEndDate(toFormatted);
                            }}
                            maxDate={todayDate}
                            minDate={thirtyDaysAgoDate}
                            numberOfMonths={1}
                            allowManualInput={true}
                            showMonthYearDropdown={false}
                        />
                    </div>
                </div>
                <div className={'ingestion-logs-divider'}/>
                <div className={'checkbox-group'}>
                    <h6 className={'checkbox-header'}>Status</h6>
                    <div className={'checkbox-checkboxes'}>
                        <Checkbox labelText={'Successful'}
                                  onChange={() => {
                                      updateIncludeSuccess(!includeSuccess);
                                  }}
                                  checked={includeSuccess}/>
                        <Checkbox labelText={'Failed'}
                                  onChange={() => {
                                      updateIncludeFailures(!includeFailures);
                                  }}
                                  checked={includeFailures}/>
                        <Checkbox labelText={'Running'}
                                  onChange={() => {
                                      updateIncludeCurrentlyRunning(!includeCurrentlyRunning);
                                  }}
                                  checked={includeCurrentlyRunning}/>
                    </div>
                </div>
                <div className={'ingestion-logs-divider'}/>
                {userTypeFromToken !== userType.locatorsUser &&
                    <>
                        <div className={'checkbox-group'}>
                            <h6 className={'checkbox-header'}>Advanced</h6>
                            <div className={'checkbox-checkboxes'}>
                                <Checkbox labelText={'Bulk Loads Only'}
                                          onChange={() => {
                                              updateIncludeBulkOnly(!includeBulkOnly);
                                          }}
                                          checked={includeBulkOnly}/>
                                <Checkbox labelText={'Short Circuits'}
                                          onChange={() => {
                                              updateIncludeShortCircuit(!includeShortCircuit);
                                          }}
                                          checked={includeShortCircuit}/>
                                <Checkbox labelText={'Hide Onboarding'}
                                          onChange={() => {
                                              updateHideOnboarding(!hideOnboarding);
                                          }}
                                          checked={hideOnboarding}/>
                            </div>
                        </div>
                        <div className={'ingestion-logs-divider'}/>
                    </>
                }
                <button className={'button primary left-margin'}
                        disabled={shouldDisableButton()}
                        onClick={() => {
                            updateIngestionLogsParameters({
                                includeSuccess,
                                includeShortCircuit,
                                includeBulkOnly,
                                includeCurrentlyRunning,
                                includeFailures,
                                startDate,
                                endDate,
                                retailerIds: retailerId,
                                hideOnboarding,
                                submitCount: ingestionLogsParameters.submitCount + 1
                            })
                        }}>Submit
                </button>
            </div>
            <IngestionLogsPageKey/>
            <LoadingWrapper loading={salesForceLoading || loading}>
                <IngestionLogsContent data={ingestionLogsData}
                                      salesforceRetailers={salesforceRetailers.salesforceRetailers}
                                      hideOnboarding={hideOnboarding}/>
            </LoadingWrapper>
        </Page>
    )
}


IngestionLogsPage.propTypes = {
    displayHelmetHeader: PropTypes.bool,
    showAirflowMetrics: PropTypes.bool,
    retailerId: PropTypes.number
}


export default IngestionLogsPage;